import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Gift,
  Star,
  Zap,
  Trophy,
  Loader2,
  ArrowLeft,
  User,
  TrendingUp,
} from "lucide-react";
import {
  rewardsService,
  PublicRewardProfile,
} from "@/services/rewards.service";
import { toast } from "react-hot-toast";
import { capitalize } from "lodash";
import { formatCurrency } from "@/utils/currency";

export default function PublicRewardProfilePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<PublicRewardProfile | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) {
        toast.error("Invalid reward profile link");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await rewardsService.getPublicRewardProfile(token);
        setProfileData(data);
      } catch (error) {
        console.error("Failed to fetch public profile:", error);
        toast.error("Failed to load reward profile");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-600 via-brand-500 to-pink-500">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading reward profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-600 via-brand-500 to-pink-500">
        <div className="text-center text-white max-w-md mx-auto p-6">
          <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h1 className="text-2xl font-bold mb-2">Reward Profile Not Found</h1>
          <p className="mb-6 text-white/80">
            The reward profile you're looking for doesn't exist or the link may
            be invalid.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-card text-brand-600 hover:bg-card/90"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const currentTier = profileData.rewardTiers[profileData.currentTierIndex];
  const nextTierIndex =
    profileData.currentTierIndex < profileData.rewardTiers.length - 1
      ? profileData.currentTierIndex + 1
      : null;
  const nextTier =
    nextTierIndex !== null ? profileData.rewardTiers[nextTierIndex] : null;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-500 to-pink-500" />
      <div className="bg-black/20 absolute inset-0" />

      <div className="relative z-10 p-4 sm:p-6 lg:p-8 max-w-[100%] mx-auto">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Public Reward Profile
          </h1>
          <p className="text-sm sm:text-base text-white/80">
            {profileData.userType === "affiliate" ? "Affiliate" : "Brand"}{" "}
            Achievements and Milestones
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Profile Card */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* User Info Card */}
            <Card className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-white/20 to-white/10 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    {profileData.name}
                  </h2>
                  <p className="text-sm text-white/70 capitalize">
                    {profileData.userType}
                  </p>
                </div>
              </div>

              {/* Current Achievement */}
              <div className="bg-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8">
                {profileData.currentReward ? (
                  <>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-brand-100 to-brand-200 rounded-xl sm:rounded-2xl flex items-center justify-center text-3xl sm:text-4xl shadow-lg">
                        <img
                          className="scale-125 rotate-[10deg]"
                          src="/reward-assets/reward-pictures/trophy.png"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm text-muted-foreground mb-1">
                          ${profileData.totalEarnings.toLocaleString()}{" "}
                          {profileData.userType === "affiliate"
                            ? "earned"
                            : "revenue generated"}
                        </p>
                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
                          {profileData.currentReward.tier.toUpperCase()}
                        </h3>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {nextTier && (
                      <>
                        <div className="mb-2 sm:mb-3">
                          <div className="w-full bg-gradient-to-r from-brand-100 to-pink-100 rounded-full h-2 sm:h-3 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-brand-600 to-pink-500 h-2 sm:h-3 rounded-full transition-all duration-500"
                              style={{
                                width: `${Math.min(
                                  profileData.progress,
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
                          <span className="font-medium">
                            ${profileData.totalEarnings.toLocaleString()}{" "}
                            generated
                          </span>
                        </div>
                      </>
                    )}

                    {/* Rewards List */}
                    <div className="mt-4 bg-gradient-to-r from-brand-50 to-pink-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-brand-100">
                      <p className="text-xs sm:text-sm font-semibold text-foreground mb-2">
                        Rewards:
                      </p>
                      <div className="space-y-1">
                        {profileData.currentReward.rewardTypes.map(
                          (reward, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Gift className="w-3 h-3 text-brand-600" />
                              <span className="text-xs sm:text-sm text-muted-foreground">
                                {reward}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Trophy className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No rewards earned yet</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Stats Card */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Card className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-card/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-white">
                      {currentTier.tier}
                    </p>
                    <p className="text-xs text-white/80">Current Tier</p>
                  </div>
                </div>
              </Card>

              <Card className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-card/20 rounded-xl flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-white">
                      {formatCurrency(profileData.totalEarnings)}
                    </p>
                    <p className="text-xs text-white/80">Total Earned</p>
                  </div>
                </div>
              </Card>
            </div>
            <div className="mt-8 text-center">
              <Card className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  Want to earn rewards like this?
                </h3>
                <p className="text-white/80 mb-4">
                  Join ReveShare and start earning exclusive rewards today!
                </p>
                <Button
                  onClick={() => navigate("/auth/login")}
                  className="bg-card text-brand-600 hover:bg-card/90 font-semibold px-8"
                >
                  Get Started
                </Button>
              </Card>
            </div>
          </div>

          {/* Milestone Rewards Sidebar */}
          <div className="lg:col-span-1">
            <Card
              id="RL-milestone-sidebar"
              className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:sticky lg:top-8"
            >
              <h2 className="text-base sm:text-lg font-semibold text-white mb-2">
                Milestone Rewards
              </h2>
              <p className="text-xs sm:text-sm text-white/80">
                Achievement Journey
              </p>

              <div className="space-y-2 sm:space-y-3">
                {profileData.rewardTiers.map((milestone, index) => {
                  const isCompleted =
                    profileData.totalEarnings >= milestone.threshold;
                  const isCurrent = index === profileData.currentTierIndex;

                  return (
                    <div
                      key={index}
                      className={`rounded-xl sm:rounded-2xl p-3 sm:p-4 transition-all duration-300 ${
                        isCurrent
                          ? "bg-card border-2 border-white shadow-lg scale-105"
                          : isCompleted
                          ? "bg-card/90 border border-white/50"
                          : "bg-card/20 border border-white/30"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-xl sm:text-2xl ${
                            isCurrent || isCompleted
                              ? "bg-gradient-to-br from-brand-100 to-pink-100"
                              : "bg-card/20"
                          }`}
                        >
                          <img
                            src={`/reward-assets/reward-pictures/${
                              index + 1
                            }.png`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3
                              className={`text-sm sm:text-base font-bold ${
                                isCurrent || isCompleted
                                  ? "text-foreground"
                                  : "text-white"
                              }`}
                            >
                              {milestone.tier}
                            </h3>
                            <span
                              className={`text-xs sm:text-sm font-semibold ${
                                isCurrent || isCompleted
                                  ? "text-foreground"
                                  : "text-white"
                              }`}
                            >
                              {formatCurrency(milestone.threshold)}
                            </span>
                          </div>
                          <p
                            className={`text-xs truncate ${
                              isCurrent || isCompleted
                                ? "text-muted-foreground"
                                : "text-white/80"
                            }`}
                          >
                            {milestone.rewards.map((e) => {
                              return (
                                <div key={e}>
                                  - {capitalize(e.replace(/_/g, " "))}
                                </div>
                              );
                            })}
                          </p>
                        </div>
                        {isCompleted && (
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-white fill-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Next Milestone Info */}
              {nextTier && (
                <Card
                  id="RL-next-reward-info"
                  className="mt-4 sm:mt-6 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 border border-yellow-400/30 rounded-xl sm:rounded-2xl p-3 sm:p-4"
                >
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-800" />
                    <h3 className="text-xs sm:text-sm font-semibold text-amber-900">
                      Next Milestone
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {nextTier.rewards.map((reward, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Gift className="w-3 h-3 text-amber-900/80" />
                        <span className="text-xs text-amber-900/90">
                          {capitalize(reward.replace(/_/g, " "))}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {!nextTier && (
                <Card className="mt-4 sm:mt-6 bg-gradient-to-br from-green-400/20 to-emerald-400/20 border border-green-400/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />
                    <h3 className="text-xs sm:text-sm font-semibold text-white">
                      Maximum Tier!
                    </h3>
                  </div>
                  <p className="text-xs text-white/90">
                    Reached the highest tier available.
                  </p>
                </Card>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
